/// <reference types="new-relic-browser" />
import ErrorState from 'components/error-state';
import Layout from 'components/layout';
import { NextPageContext } from 'next';
import { HttpStatusCode } from 'axios';
import { getThemeOverrideFromRequest } from 'utils/themes';
import { isClientSide, isServerSide } from 'utils/host-config';
import { ThemeNames } from 'types/themes';
import logger from 'utils/logger';


type ErrorProps = {
  /** HTTP status code */
  statusCode: number;
  /** HTTP status message */
  statusMessage: string;
  /** Error object if encountered during rendering */
  err?: Error;
  /** `String` of the actual path including query. */
  asPath?: string;
  /** Tenant ID */
  tenant: string;
};

function ErrorPage({ tenant, statusCode, statusMessage, err, asPath }: ErrorProps) {
  // Notify New Relic about the error
  if (isClientSide() && window.newrelic && window.newrelic.noticeError) {
    const errorToReport = err || new Error(`${tenant} - ${statusCode} ${statusMessage}`);
    window.newrelic.noticeError(errorToReport, {
      statusCode,
      statusMessage,
      asPath,
      tenant,
    });
  }

  return (
    <Layout>
      <ErrorState
        image={{
          src: tenant === ThemeNames.ZOOCASA ? '/assets/images/oops.svg' : '/assets/images/oops-exp.svg', // FIXME: Add to tenant config
          altText: 'Internal Server Error',
        }}
        title="Oops! Internal Server Error!"
        description="Something went wrong on the website's server, but it's not your fault."
        cta={{
          text: 'Back to Homepage',
          onClick: () => window.location.href = '/',
        }}
      /></Layout>
  );
}

ErrorPage.getInitialProps = ({ req, res, err, asPath }: NextPageContext) => {
  const tenant = getThemeOverrideFromRequest(res?.req) || ThemeNames.ZOOCASA;

  // Prioritize error object properties over response properties
  const statusCode = err?.statusCode || res?.statusCode || HttpStatusCode.InternalServerError;
  const statusMessage = err?.message || res?.statusMessage || 'Internal Server Error';

  if (isServerSide()) {
    /** FIXME: Temporarily print headers to investigate problems with cookies */
    const headersObject = Object.fromEntries(Object.entries(req?.headers)) || {};
    logger.error({
      error: err || `${tenant} - ${statusCode} ${statusMessage}`,
      statusCode,
      statusMessage,
      headersObject,
      asPath,
    }, `Unhandled error: ${err || `${tenant} - ${statusCode} ${statusMessage}`}`);
  } else {
    logger.error({
      error: err || `${tenant} - ${statusCode} ${statusMessage}`,
      statusCode,
      statusMessage,
      asPath,
    }, `Unhandled error: ${err || `${tenant} - ${statusCode} ${statusMessage}`}`);
  }

  const props: ErrorProps = {
    tenant,
    statusCode,
    statusMessage,
    err,
    asPath,
  };
  return props;

};

export default ErrorPage;
